"use client";

import { proxy } from "valtio";

import type { Bridge } from "./bridge";
import { getAdSourceFromStore } from "@/src/utils/onboarding/adSourceStore";

/** 与后端约定：Adjust SDK 下发的 adid（UUID），勿服务端生成 */
const HEADER_OS_ADJUST_AD_ID = "OS-Adjust-Ad-Id" as const;
/** Android GAID；iOS 可不传此头 */
const HEADER_OS_ADVERTISING_ID = "OS-Advertising-Id" as const;
/** iOS IDFA；未授权可为空，接入原生后写入 */
const HEADER_OS_IDFA = "OS-Idfa" as const;
/** 广告来源（ad_source，从内存 store 取，不从 cookie） */
const HEADER_OS_AD_SOURCE = "OS-Ad-Source" as const;

/**
 * 登录 / 支付侧请求可合并的「设备广告相关」请求头状态。
 * - adjustAdId：由 Bridge `adjust-get-adid` 同步
 * - advertisingId：Android 由 Bridge `os-get-advertising-id`（GAID）同步；iOS 无 GAID
 * - idfa：预留 iOS IDFA
 */
const osAttributionHeaderState = proxy<{
  adjustAdId: string | null;
  advertisingId: string | null;
  idfa: string | null;
}>({
  adjustAdId: null,
  advertisingId: null,
  idfa: null,
});

function getOsAttributionRequestHeaders(): Record<string, string> {
  const out: Record<string, string> = {};
  const { adjustAdId, advertisingId, idfa } = osAttributionHeaderState;

  if (adjustAdId && adjustAdId.length > 0) {
    out[HEADER_OS_ADJUST_AD_ID] = adjustAdId;
  }
  if (advertisingId && advertisingId.length > 0) {
    out[HEADER_OS_ADVERTISING_ID] = advertisingId;
  }
  // IDFA 允许显式传空字符串（用户未授权追踪）；null 表示未接入、不下发该头
  if (idfa !== null) {
    out[HEADER_OS_IDFA] = idfa;
  }
  // 广告来源（从内存 store 取，有值才带）
  const adSource = getAdSourceFromStore();
  if (adSource) {
    out[HEADER_OS_AD_SOURCE] = adSource;
  }

  return out;
}

/** 合并登录/支付请求头：业务 headers + OS-Adjust-Ad-Id / OS-Advertising-Id / OS-Idfa / OS-Ad-Source（有值才带） */
export function mergeAdjustAdidIntoHeaders(
  headers: Record<string, string> = {},
): Record<string, string> {
  return { ...headers, ...getOsAttributionRequestHeaders() };
}

function setAdjustAdidCache(adid: string | null): void {
  osAttributionHeaderState.adjustAdId =
    typeof adid === "string" && adid.length > 0 ? adid : null;
}

/** 写入 GAID 缓存（通常由 {@link syncOsAdvertisingIdFromBridge} 调用） */
function setOsAdvertisingIdCache(id: string | null): void {
  osAttributionHeaderState.advertisingId =
    typeof id === "string" && id.length > 0 ? id : null;
}

/** 预留：iOS IDFA 接入后调用；传 "" 表示未授权等场景（仍会带上 OS-Idfa 头） */
function setOsIdfaCache(id: string | null): void {
  osAttributionHeaderState.idfa = id;
}

/** App 内拉取 ADID 并写入缓存，供登录/支付请求头合并使用 */
export async function syncAdjustAdidFromBridge(
  bridge: Pick<Bridge, "isInApp" | "adjustGetAdid">,
): Promise<void> {
  if (!bridge.isInApp()) {
    setAdjustAdidCache(null);
    return;
  }
  try {
    const adid = await bridge.adjustGetAdid();
    setAdjustAdidCache(adid);
  } catch {
    setAdjustAdidCache(null);
  }
}

/** App 内拉取 Android GAID（`os-get-advertising-id`）并写入缓存；仅 Android 会调 Bridge，iOS 直接清空缓存 */
export async function syncOsAdvertisingIdFromBridge(
  bridge: Pick<Bridge, "isInApp" | "getPlatform" | "osGetAdvertisingId">,
): Promise<void> {
  if (!bridge.isInApp()) {
    setOsAdvertisingIdCache(null);
    return;
  }
  if (bridge.getPlatform() !== "android") {
    setOsAdvertisingIdCache(null);
    return;
  }
  try {
    const data = await bridge.osGetAdvertisingId();
    const raw = data?.advertisingId;
    const id = typeof raw === "string" && raw.length > 0 ? raw : null;
    setOsAdvertisingIdCache(id);
  } catch {
    setOsAdvertisingIdCache(null);
  }
}

/** App 内拉取 iOS ATT + IDFA（`request-app-tracking-transparency`）并写入缓存；仅 iOS 会调 Bridge。 */
export async function syncOsIdfaFromBridge(
  bridge: Pick<
    Bridge,
    "isInApp" | "getPlatform" | "requestAppTrackingTransparency"
  >,
): Promise<void> {
  if (!bridge.isInApp()) {
    setOsIdfaCache(null);
    return;
  }
  if (bridge.getPlatform() !== "ios") {
    setOsIdfaCache(null);
    return;
  }
  try {
    const data = await bridge.requestAppTrackingTransparency();
    const rawIdfa = data?.idfa;
    const idfa =
      typeof rawIdfa === "string" && rawIdfa.length > 0 ? rawIdfa : "";
    // iOS 需要保证头存在：授权后带真实 IDFA，未授权显式传空字符串。
    setOsIdfaCache(idfa);
  } catch {
    // 调用失败时不下发该头，避免把异常当成“明确未授权”。
    setOsIdfaCache(null);
  }
}
